import { useMemo, useState, useEffect, useRef } from "react";
import { MapPin, Loader2, RefreshCw, AlertCircle, Grid3x3 } from "lucide-react";
import { toast } from "sonner";
import { SEV_META, prettyType } from "@/lib/defects";

const GC = 6, GR = 5;
function bayRef(x, z, b) {
  const c = Math.max(0, Math.min(Math.floor(((x - b.minx) / b.spanx) * GC), GC - 1));
  const r = Math.max(0, Math.min(Math.floor(((b.maxz - z) / b.spanz) * GR), GR - 1));
  return String.fromCharCode(65 + c) + (r + 1);
}

// Real scan floorplan (captured from Matterport) with defect pins projected on top.
// Auto-captures on open; falls back to an abstract position grid only if the scan can't be read.
export function FloorPlan({ defects, numberOf, levelName, unitLabel, selectedId, onSelect, onCaptureFloorplan }) {
  const [status, setStatus] = useState("idle"); // idle | capturing | ready | failed
  const [bg, setBg] = useState(null); // { image, points:[{id,xPct,yPct}], w, h }
  const [showGrid, setShowGrid] = useState(false);
  const attempted = useRef(false);

  const svgRef = useRef(null);
  const [view, setView] = useState({ k: 1, x: 0, y: 0 });
  const drag = useRef(null);

  const toSvg = (e) => {
    const svg = svgRef.current; if (!svg?.createSVGPoint) return null;
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return null;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };
  const onWheel = (e) => {
    e.preventDefault(); const p = toSvg(e); if (!p) return;
    setView((v) => {
      const k = Math.min(8, Math.max(1, v.k * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
      const wx = (p.x - v.x) / v.k, wy = (p.y - v.y) / v.k;
      return { k, x: p.x - wx * k, y: p.y - wy * k };
    });
  };
  const onDown = (e) => { drag.current = toSvg(e); };
  const onMove = (e) => {
    if (!drag.current) return; const p = toSvg(e); if (!p) return;
    setView((v) => ({ ...v, x: v.x + (p.x - drag.current.x), y: v.y + (p.y - drag.current.y) }));
    drag.current = p;
  };
  const onUp = () => { drag.current = null; };
  const zoomStep = (f, VBW, VBH) => setView((v) => {
    const k = Math.min(8, Math.max(1, v.k * f));
    const cx = VBW / 2, cy = VBH / 2, wx = (cx - v.x) / v.k, wy = (cy - v.y) / v.k;
    return { k, x: cx - wx * k, y: cy - wy * k };
  });
  const resetView = () => setView({ k: 1, x: 0, y: 0 });

  const pinned = useMemo(
    () => defects.filter((d) => d.anchor3d?.position && d.anchor3d.position.x != null && d.anchor3d.position.z != null),
    [defects]
  );

  const bounds = useMemo(() => {
    if (!pinned.length) return null;
    const xs = pinned.map((d) => d.anchor3d.position.x);
    const zs = pinned.map((d) => d.anchor3d.position.z);
    const minx = Math.min(...xs), maxx = Math.max(...xs);
    const minz = Math.min(...zs), maxz = Math.max(...zs);
    return { minx, maxx, minz, maxz, spanx: Math.max(maxx - minx, 0.5), spanz: Math.max(maxz - minz, 0.5) };
  }, [pinned]);

  const capture = async () => {
    if (!onCaptureFloorplan) { setStatus("failed"); return; }
    setStatus("capturing");
    try {
      const wps = pinned.map((d) => ({ id: d.id, position: d.anchor3d.position }));
      const res = await onCaptureFloorplan(wps);
      if (res?.image) { setBg(res); setStatus("ready"); }
      else { setStatus("failed"); }
    } catch (e) {
      setStatus("failed");
    }
  };

  useEffect(() => {
    if (!attempted.current && onCaptureFloorplan) {
      attempted.current = true;
      capture();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => { setBg(null); setShowGrid(false); resetView(); capture(); };

  // Pins projected onto the captured floorplan image (percent coords → viewBox coords)
  const bgView = useMemo(() => {
    if (!bg) return null;
    const VBW = 1000;
    const VBH = bg.w && bg.h ? Math.round((1000 * bg.h) / bg.w) : 680;
    const map = {};
    (bg.points || []).forEach((p) => { map[p.id] = p; });
    const dots = pinned
      .map((d, i) => {
        const p = map[d.id];
        if (!p || p.xPct == null || p.xPct < -0.08 || p.xPct > 1.08 || p.yPct < -0.08 || p.yPct > 1.08) return null;
        return {
          d, n: numberOf?.[d.id] ?? i + 1,
          bay: bounds ? bayRef(d.anchor3d.position.x, d.anchor3d.position.z, bounds) : "",
          cx: p.xPct * VBW, cy: p.yPct * VBH,
        };
      })
      .filter(Boolean);
    return { VBW, VBH, dots, placed: dots.length };
  }, [bg, pinned, numberOf, bounds]);

  const Legend = () => (
    <div className="hidden sm:flex items-center gap-3">
      {Object.entries(SEV_META).map(([k, m]) => (
        <span key={k} className="flex items-center gap-1 text-[10px] text-white/70">
          <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: m.color }} /> {m.label}
        </span>
      ))}
    </div>
  );

  const Dot = ({ dot, onImage }) => {
    const m = SEV_META[dot.d.severity] || SEV_META.medium;
    const sel = selectedId === dot.d.id;
    return (
      <g data-testid={`plan-dot-${dot.d.id}`} onClick={() => onSelect?.(dot.d)} style={{ cursor: "pointer" }}>
        {sel && <circle cx={dot.cx} cy={dot.cy} r={24} fill="none" stroke="#C5A24A" strokeWidth="3" />}
        <circle cx={dot.cx} cy={dot.cy} r={15} fill={m.color} stroke="#000" strokeWidth="2.5" />
        <text x={dot.cx} y={dot.cy} textAnchor="middle" dominantBaseline="central" fontSize="15" fontWeight="700" fill="#fff">{dot.n}</text>
        {dot.bay && <text x={dot.cx} y={dot.cy + 27} textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff" stroke="#000" strokeWidth="0.5">{dot.bay}</text>}
        <title>{`#${dot.n}${dot.bay ? ` · Bay ${dot.bay}` : ""} · ${dot.d.title} — ${m.label} ${prettyType(dot.d.defect_type)}`}</title>
      </g>
    );
  };

  return (
    <div className="absolute inset-0 z-[16] bg-[#0A0A0A] flex flex-col" data-testid="floor-plan-view">
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#C5A24A]" />
          <span className="text-sm font-semibold">Defect Location Plan</span>
          <span className="text-xs text-muted-foreground">· {levelName || unitLabel}</span>
          {status === "ready" && bgView && (
            <span className="text-[10px] text-muted-foreground">· {bgView.placed}/{pinned.length} pins placed</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {status === "ready" && (
            <button onClick={reload} data-testid="reload-floorplan-btn" className="flex items-center gap-1.5 text-[11px] px-3 py-1 rounded-full border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              <RefreshCw className="w-3 h-3" /> Reload
            </button>
          )}
          <Legend />
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        {!pinned.length ? (
          <div className="text-center max-w-sm" data-testid="floor-plan-empty">
            <MapPin className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No pinned defects on this {(unitLabel || "level").toLowerCase()} yet. Pin defects to the 3D scan
              (Scan tab → select a defect → <span className="text-white">Pin to this spot</span>) and they'll appear on the floorplan here.
            </p>
          </div>
        ) : status === "capturing" ? (
          <div className="text-center" data-testid="floor-plan-loading">
            <Loader2 className="w-8 h-8 text-[#C5A24A] mx-auto mb-3 animate-spin" />
            <p className="text-sm text-muted-foreground">Building the floorplan from the scan…</p>
          </div>
        ) : status === "ready" && bg && bgView ? (
          <div className="relative w-full h-full">
            <svg ref={svgRef} viewBox={`0 0 ${bgView.VBW} ${bgView.VBH}`} className="w-full h-full" data-testid="floor-plan-svg"
                 style={{ userSelect: "none", cursor: "grab", touchAction: "none" }}
                 onWheel={onWheel} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
              <g transform={`translate(${view.x} ${view.y}) scale(${view.k})`}>
                <image href={bg.image} x="0" y="0" width={bgView.VBW} height={bgView.VBH} preserveAspectRatio="none" data-testid="floor-plan-bg" />
                {bgView.dots.map((dot) => <Dot key={dot.d.id} dot={dot} />)}
              </g>
            </svg>
            <div className="absolute bottom-3 right-3 flex flex-col gap-1.5">
              <button onClick={() => zoomStep(1.3, bgView.VBW, bgView.VBH)} data-testid="plan-zoom-in" className="w-8 h-8 rounded-full bg-black/70 border border-white/15 text-white text-lg leading-none hover:bg-black/90 transition-colors">+</button>
              <button onClick={() => zoomStep(1 / 1.3, bgView.VBW, bgView.VBH)} data-testid="plan-zoom-out" className="w-8 h-8 rounded-full bg-black/70 border border-white/15 text-white text-lg leading-none hover:bg-black/90 transition-colors">−</button>
              <button onClick={resetView} data-testid="plan-zoom-reset" className="w-8 h-8 rounded-full bg-black/70 border border-white/15 text-white/80 hover:bg-black/90 transition-colors flex items-center justify-center"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            {bgView.placed === 0 && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[11px] bg-black/75 px-3 py-1 rounded-full text-[#FF9500]" data-testid="plan-no-pins">
                Floorplan loaded, but no pins projected — hit Reload once the scan has fully settled.
              </div>
            )}
            <div className="absolute bottom-3 left-3 text-[10px] text-white/50 bg-black/50 px-2 py-1 rounded">Scroll to zoom · drag to pan</div>
          </div>
        ) : showGrid ? (
          <GridFallback pinned={pinned} bounds={bounds} numberOf={numberOf} Dot={Dot} />
        ) : (
          <div className="text-center max-w-md" data-testid="floor-plan-failed">
            <AlertCircle className="w-8 h-8 text-[#FF9500] mx-auto mb-3" />
            <p className="text-sm text-white font-medium mb-1">Couldn't load the scan floorplan</p>
            <p className="text-xs text-muted-foreground mb-4">
              Make sure the 3D scan has finished loading, that your Matterport SDK key is an <span className="text-white">SDK&nbsp;Bundle</span> key,
              and that this site's domain is on the key's allow‑list. Then try again.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={reload} data-testid="retry-floorplan-btn" className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#C5A24A] text-black font-semibold hover:bg-[#A8863A] transition-colors">
                <RefreshCw className="w-3 h-3" /> Try again
              </button>
              <button onClick={() => setShowGrid(true)} data-testid="show-grid-btn" className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                <Grid3x3 className="w-3 h-3" /> Show basic position grid
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Abstract, to-scale position grid — fallback only when the real scan floorplan is unavailable.
function GridFallback({ pinned, bounds, numberOf, Dot }) {
  const layout = useMemo(() => {
    if (!bounds) return null;
    const { minx, maxz, spanx, spanz } = bounds;
    const W = 1000, H = 680, pad = 70;
    const availW = W - 2 * pad, availH = H - 2 * pad;
    const scale = Math.min(availW / spanx, availH / spanz);
    const offx = pad + (availW - spanx * scale) / 2;
    const offy = pad + (availH - spanz * scale) / 2;
    const dots = pinned.map((d, i) => ({
      d, n: numberOf?.[d.id] ?? i + 1, bay: bayRef(d.anchor3d.position.x, d.anchor3d.position.z, bounds),
      cx: offx + (d.anchor3d.position.x - minx) * scale, cy: offy + (maxz - d.anchor3d.position.z) * scale,
    }));
    return { W, H, pad, offx, offy, cw: spanx * scale, ch: spanz * scale, dots };
  }, [pinned, bounds, numberOf]);
  if (!layout) return null;
  return (
    <svg viewBox={`0 0 ${layout.W} ${layout.H}`} className="w-full h-full max-w-4xl" data-testid="floor-plan-svg" style={{ userSelect: "none" }}>
      <rect x={layout.offx} y={layout.offy} width={layout.cw} height={layout.ch} fill="#111111" stroke="#2A2A2A" strokeWidth="2" />
      {Array.from({ length: GC + 1 }).map((_, c) => (
        <line key={`v${c}`} x1={layout.offx + (layout.cw * c) / GC} y1={layout.offy} x2={layout.offx + (layout.cw * c) / GC} y2={layout.offy + layout.ch} stroke="#1E1E1E" strokeWidth="1" />
      ))}
      {Array.from({ length: GR + 1 }).map((_, r) => (
        <line key={`h${r}`} x1={layout.offx} y1={layout.offy + (layout.ch * r) / GR} x2={layout.offx + layout.cw} y2={layout.offy + (layout.ch * r) / GR} stroke="#1E1E1E" strokeWidth="1" />
      ))}
      {Array.from({ length: GC }).map((_, c) => (
        <text key={`cl${c}`} x={layout.offx + (layout.cw * (c + 0.5)) / GC} y={layout.offy - 12} fill="#6B7280" fontSize="15" textAnchor="middle">{String.fromCharCode(65 + c)}</text>
      ))}
      {Array.from({ length: GR }).map((_, r) => (
        <text key={`rl${r}`} x={layout.offx - 16} y={layout.offy + (layout.ch * (r + 0.5)) / GR} fill="#6B7280" fontSize="15" textAnchor="middle" dominantBaseline="central">{r + 1}</text>
      ))}
      {layout.dots.map((dot) => <Dot key={dot.d.id} dot={dot} />)}
      <text x={layout.W / 2} y={layout.H - 24} fill="#6B7280" fontSize="13" textAnchor="middle">Abstract position grid (real scan floorplan unavailable)</text>
    </svg>
  );
}
